/**
 * Copyright ToolBelt Data Inc., 2021 All Rights Reserved
 */

import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';

import styled from 'styled-components';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faTimes, faLock, faLockOpen } from '@fortawesome/free-solid-svg-icons';
import { APP_COLORS } from "../../../../common/Constants";

import { Tooltip as ReactTooltip } from "react-tooltip";

const TYPE_MAPPINGS = {
    'DATE': 'BEFORE',
    'CONTAINS': 'CONTAINS',
    'DROP_DOWN': 'IN',
    'HAS_VALUE': 'IS',
    'RANGE': 'BETWEEN',
    // Add more here if needed, e.g., 'EQUALS': 'IS'
};

// --- Styled Components ---
const Wrapper = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: space-between;

    max-width: 100%;
    min-height: 45px;
    margin-right: 10px;
    margin-bottom: 10px;
    padding: 6px 12px;

    border: 1px solid ${APP_COLORS['gray-600'] || '#6c757d'};
    border-radius: 0.25rem;

    background-color: ${props => {
        if (props.customColor) return props.customColor;
        return props.isLocked ? 'rgb(233, 236, 239)' : 'rgb(244, 240, 226)';
    }};

    transition: all 0.2s ease-in-out;
    white-space: nowrap;
`;

const InfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin-right: 10px;
    overflow: hidden;
`;

const Label = styled.span`
    font-size: 0.65rem;
    font-weight: 600;
    color: ${APP_COLORS['gray-600'] || '#6c757d'};
    text-transform: uppercase;
    margin-bottom: 2px;
    line-height: 1.2;
`;

const ValueContainer = styled.div`
    font-size: 0.8rem;
    color: ${APP_COLORS['gray-600'] || '#6c757d'};
    display: flex;
    align-items: center;
`;

const TypeLabel = styled.span`
    font-weight: 700;
    color: ${APP_COLORS['tech-blue'] || '#00a4e4'}; /* Use a distinct color */
    margin-right: 4px;
    font-size: 0.75rem;
    text-transform: uppercase;
`;

const ValueText = styled.span`
    /* Standard value styling */
`;

const MoreTrigger = styled.span`
    cursor: pointer;
    margin-left: 4px;
    font-weight: bold;
    color: ${APP_COLORS['info'] || '#17a2b8'};

    &:hover {
        text-decoration: underline;
    }
`;

const ActionButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    margin-left: auto;
    cursor: ${props => props.disabled ? 'default' : 'pointer'};
    opacity: ${props => props.disabled ? 0.5 : 1};
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        opacity: ${props => props.disabled ? 0.5 : 0.7};
    }

    &:focus {
        outline: none;
    }
`;

const DetailPopup = styled.div`
    font-size: 12px;
    max-width: 300px;
    word-wrap: break-word;
    white-space: normal;
`;

// --- Main Component ---
const FilterChip = (props) => {
    const {
        name,
        detail,
        type,
        id,
        handleRemove,
        isLocked,
        lockOpen,
        color
    } = props;

    const [displayDetail, setDisplayDetail] = useState('');
    const [moreDetail, setMoreDetail] = useState(false);

    // Format the type label (optional: replace underscores with spaces)
    const displayType = TYPE_MAPPINGS[type] || (type ? type.replace(/_/g, ' ') : '');

    const getCleanDetail = (rawDetail) => {
        if (!rawDetail) return '';

        let parsedData = rawDetail;

        if (typeof rawDetail === 'string' && rawDetail.trim().startsWith('[') && rawDetail.trim().endsWith(']')) {
            try {
                parsedData = JSON.parse(rawDetail);
            } catch (e) {
                return rawDetail;
            }
        }

        if (Array.isArray(parsedData)) {
            return parsedData.map(item => item?.display || item?.id || item).join(', ');
        }

        return String(rawDetail);
    };

    useEffect(() => {
        const cleanDetailString = getCleanDetail(detail);
        const fullText = `${displayType} ${cleanDetailString}`;

        if (fullText.length > 50) {
            setDisplayDetail(cleanDetailString.substring(0, 40));
            setMoreDetail(true);
        } else {
            setDisplayDetail(cleanDetailString);
            setMoreDetail(false);
        }
    }, [detail, displayType]);

    const renderActionIcon = () => {
        if (isLocked) {
            return <FontAwesomeIcon style={{fontSize: "12px"}} color={APP_COLORS['gray-600']}
                                    icon={lockOpen ? faLockOpen : faLock}/>;
        }
        return <FontAwesomeIcon style={{fontSize: "14px"}} color={APP_COLORS['gray-600']} icon={faTimes}/>;
    };

    return (
        <Wrapper isLocked={isLocked} customColor={color}>
            <InfoContainer>
                <Label>{name}</Label>
                <ValueContainer>
                    {/* Render Type Label if it exists */}
                    {displayType && <TypeLabel>{displayType}</TypeLabel>}

                    <ValueText>
                        {displayDetail}
                    </ValueText>

                    {moreDetail && (
                        <>
                            <MoreTrigger
                                data-tooltip-id={`tooltip-${id}`}
                                className="detail-button"
                            >
                                ...
                            </MoreTrigger>
                            <ReactTooltip
                                id={`tooltip-${id}`}
                                place='bottom'
                                clickable={true}
                                style={{zIndex: 9999, opacity: 1}}
                            >
                                {/* Tooltip shows full Type + Value */}
                                <DetailPopup>
                                    <strong>{displayType}</strong> {detail}
                                </DetailPopup>
                            </ReactTooltip>
                        </>
                    )}
                </ValueContainer>
            </InfoContainer>

            <ActionButton
                disabled={isLocked}
                onClick={(e) => {
                    e.preventDefault();
                    if (!isLocked) handleRemove(id);
                }}
            >
                {renderActionIcon()}
            </ActionButton>
        </Wrapper>
    );
};

FilterChip.propTypes = {
    name: PropTypes.string.isRequired,
    detail: PropTypes.string,
    type: PropTypes.string,
    handleRemove: PropTypes.func,
    isLocked: PropTypes.bool,
    lockOpen: PropTypes.bool,
    color: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

FilterChip.defaultProps = {
    isLocked: false,
    lockOpen: false,
    handleRemove: () => {
    },
    detail: '',
    type: ''
};

export default FilterChip;